import { Component, ojs, app, v, state, payload } from "modular-openscriptjs";
import { eParse, either } from "../../helpers";
import { dom, Draggable as DraggableCore } from "../../fotastart";
import { $e } from "../../ojs.events";
import { gc } from "../../declarations";
import Draggable from "../Blocks/Draggable";

const h = app("h");
const broker = app("broker");

const WIDGET_ID = "voice-order-call-widget";

// Rotates through the pre-call prompt card while it's waiting for the
// user to tap "Start Call" - a quick "here's what you can ask Yoca"
// hint. Keep phrases short (the card is only 256px wide). Rendered by
// renderCapabilityRotator() below as a pure CSS carousel - see
// voice-order-capability-cycle in app.scss for how the per-phrase
// timing/keyframes are derived from this list's length.
const CAPABILITY_PHRASES = [
    "Order by voice",
    "Add custom items",
    "Pickup & drop off",
    "Buy AI credits",
    "Activate subscription",
    "Ask for help",
    "Navigate the app",
    "Cancel orders",
    "Deposit Capa",
];
const PHRASE_ROTATION_SECONDS = 2.4;
const CAPABILITY_CYCLE_SECONDS =
    CAPABILITY_PHRASES.length * PHRASE_ROTATION_SECONDS;

/**
 * The visual half of the voice "place order" feature. The actual call
 * plumbing (WebSocket, mic, billing) lives in
 * src/classes/VoiceOrderCallClient.js and is wired to the broker by
 * src/mediators/VoiceOrderCall.js - this component only ever talks to
 * that mediator over the broker.
 *
 * Renders as a persistent, draggable floating bubble (using the existing
 * Blocks/Draggable wrapper - snaps to the nearest screen edge/corner on
 * release, same as any other draggable chip in this app) rather than a
 * modal: the whole point is that the user can keep browsing, adding
 * items, checking other orders, etc. while the call is live, and just
 * drag the bubble out of the way. Nothing here ever blocks the rest of
 * the app - there is no backdrop and no bootstrap modal involved.
 *
 * Filed under components/Modals for now since that's where it was first
 * wired into AppOffCanvas.js - it is not, in fact, a modal.
 */
export default class VoiceOrderCallModal extends Component {
    status = state("idle"); // idle | prompt | connecting | active | ending | ended | error
    transcript = state([]); // [{ speaker: 'user' | 'agent', text }] - kept for a future transcript view, not rendered right now
    billedSeconds = state(0);
    creditsCharged = state(0);
    errorMessage = state(null);
    audioLevel = state(0); // 0-1, how loud the agent is right now (drives the pulse ring)
    minimized = state(false); // in-call only: full card vs. small square badge

    // A live, second-by-second clock for the "time elapsed" display. Kept
    // separate from billedSeconds (which only jumps forward whenever a
    // heartbeat response comes back from the server, every
    // heartbeat_interval_seconds - accurate for billing, but visibly
    // "stuck" as a UI clock). This ticks locally off wall-clock time
    // instead, in its own v() so only this small text node re-renders
    // each second rather than the whole card.
    elapsedSeconds = state(0);

    endTimer = null;
    tickInterval = null;
    callStartedAt = null;

    render(...args) {
        // The voice ordering call icon only makes sense for a signed-in
        // user (it drives cart/orders/billing, none of which exist for
        // a guest) - render nothing at all when gc.auth is false, for
        // every state of the widget (idle trigger, prompt card, active
        // call card alike), not just the initial trigger button.
        return v(gc.auth, (auth) =>
            either(
                auth.value,
                () =>
                    h.Draggable(
                        {
                            id: WIDGET_ID,
                            // Starts docked at the middle of the right edge rather
                            // than the bottom-right corner, where it's more likely
                            // to sit over bottom navigation/action bars.
                            options: { snap: true, initialSnap: "center-right" },
                        },
                        this.renderBubble(),
                        ...args,
                    ),
                h.empty(),
            ),
        );
    }

    renderBubble() {
        return h.div(
            { class: "voice-order-call-bubble" },
            v(this.status, (status) => {
                if (status.value === "idle") return this.renderTrigger();
                if (status.value === "prompt") return this.renderPromptCard();

                return v(this.minimized, (min) =>
                    min.value
                        ? this.renderMinimized(status.value)
                        : this.renderCallCard(status.value),
                );
            }),
        );
    }

    renderTrigger() {
        return h.button(
            {
                class: "btn btn-fab rounded-circle text-white d-flex align-items-center justify-content-center",
                style: "background: linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%);",
                type: "button",
                title: "Order by voice",
                listeners: {
                    click: () => this.openPrompt(),
                },
            },
            h.i({ class: "bi bi-telephone-fill fs-5" }),
        );
    }

    /**
     * The light, animated "Call to Order" card shown after tapping the
     * trigger - nothing has started yet (no reservation, no socket).
     * Expanded by default (no collapsed variant): the only actions are
     * tap the green phone to actually start the call, or dismiss.
     */
    renderPromptCard() {
        return h.div(
            {
                // 16:9, small - 256x144.
                class: "voice-order-prompt-card rounded-4 shadow-lg d-flex flex-column position-relative",
                style: "width: 256px; height: 144px; padding: 10px 12px;",
            },
            // Dismiss - floated in the corner so the title can sit centered.
            h.button(
                {
                    class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                    style: "top: 8px; right: 10px;",
                    type: "button",
                    title: "Dismiss",
                    listeners: { click: () => this.closePrompt() },
                },
                h.i({ class: "fas fa-times", style: "font-size: 0.75rem;" }),
            ),
            // Title - centered, a bit bolder.
            h.div(
                {
                    class: "fw-bold text-dark text-center",
                    style: "font-size: 0.8rem;",
                },
                "Talk to YoCA",
            ),
            // Rotating "what you can ask Yoca" hint.
            this.renderCapabilityRotator(),
            // Phone button + label, vertically centered in the remaining space.
            h.div(
                {
                    class: "flex-fill d-flex flex-column align-items-center justify-content-center gap-1",
                },
                h.button(
                    {
                        class: "voice-order-pulse-btn btn rounded-circle d-flex align-items-center justify-content-center shadow border-0 text-white",
                        style: "width: 52px; height: 52px; background: linear-gradient(135deg, #34d399 0%, #059669 100%);",
                        type: "button",
                        title: "Start Call",
                        listeners: { click: () => this.startCall() },
                    },
                    h.i({ class: "bi bi-telephone-fill", style: "font-size: 1.1rem;" }),
                ),
                h.div(
                    {
                        class: "fw-semibold text-dark",
                        style: "font-size: 0.65rem;",
                    },
                    "Start Call",
                ),
            ),
            // Watermark - no background, tucked in the bottom-right corner.
            h.div(
                {
                    class: "text-end",
                    style: "font-size: 0.55rem; opacity: 0.5;",
                },
                h.i({ class: "bi bi-stars me-1" }),
                "AI powered",
            ),
        );
    }

    /**
     * The rotating "Order by voice / Add custom items / ..." hint in the
     * prompt card. Every phrase in CAPABILITY_PHRASES renders at once,
     * absolutely stacked on top of each other, and takes its turn purely
     * through a staggered CSS animation-delay (voice-order-capability-cycle
     * in app.scss) - so it loops on its own for as long as the card is on
     * screen, with no interval/state to start or tear down.
     */
    renderCapabilityRotator() {
        return h.div(
            { class: "voice-order-capability-rotator" },
            ...CAPABILITY_PHRASES.map((phrase, i) =>
                h.div(
                    {
                        class: "voice-order-capability-item text-dark",
                        style:
                            "font-size: 0.58rem; opacity: 0.65; " +
                            `animation-duration: ${CAPABILITY_CYCLE_SECONDS}s; ` +
                            `animation-delay: ${(i * PHRASE_ROTATION_SECONDS).toFixed(2)}s;`,
                    },
                    phrase,
                ),
            ),
        );
    }

    openPrompt() {
        this.status.value = "prompt";
        this.resnap();
    }

    closePrompt() {
        this.status.value = "idle";
        this.resnap();
    }

    /**
     * The in-call view - same small light 16:9 card as the prompt, just
     * with a red "End Call" and a ring around it that pulses with the
     * agent's actual voice level (see audioLevel, fed by
     * VoiceOrderCallClient's playback analyser). Reused for the brief
     * ended/error summary too, swapping the button for a neutral close.
     */
    renderCallCard(status) {
        const isLive = ["connecting", "active", "ending"].includes(status);

        return h.div(
            {
                class: "voice-order-prompt-card rounded-4 shadow-lg d-flex flex-column position-relative",
                style: "width: 256px; height: 144px; padding: 10px 12px;",
            },
            // Minimize while live, otherwise just close - floated in the
            // corner so the title can stay centered.
            h.button(
                {
                    class: "btn btn-sm btn-link text-dark opacity-75 p-0 lh-1 position-absolute",
                    style: "top: 8px; right: 10px;",
                    type: "button",
                    title: isLive ? "Minimize" : "Close",
                    listeners: {
                        click: () =>
                            isLive ? this.minimize() : this.closeBubble(),
                    },
                },
                h.i({
                    class: isLive ? "bi bi-dash-square" : "fas fa-times",
                    style: "font-size: 0.75rem;",
                }),
            ),
            // Title + subtitle (elapsed time while live, the error
            // message when there is one), centered.
            h.div(
                { class: "text-center" },
                h.div(
                    {
                        class: "fw-bold text-dark",
                        style: "font-size: 0.8rem;",
                    },
                    this.statusLabel(status),
                ),
                v(this.errorMessage, (msg) =>
                    msg.value
                        ? h.div(
                              {
                                  class: "text-danger text-truncate",
                                  style: "font-size: 0.65rem;",
                              },
                              msg.value,
                          )
                        : v(this.elapsedSeconds, (es) =>
                              h.div(
                                  {
                                      class: "text-muted",
                                      style: "font-size: 0.65rem;",
                                  },
                                  this.formatDuration(es.value),
                              ),
                          ),
                ),
            ),
            // Ring (audio-reactive while live) + phone button.
            h.div(
                {
                    class: "flex-fill d-flex flex-column align-items-center justify-content-center gap-1",
                },
                h.div(
                    { class: "position-relative", style: "width: 52px; height: 52px;" },
                    isLive
                        ? v(this.audioLevel, (level) =>
                              this.renderVoiceRing(level.value),
                          )
                        : h.empty(),
                    h.button(
                        {
                            class: "btn rounded-circle d-flex align-items-center justify-content-center shadow position-relative",
                            style: isLive
                                ? "width: 52px; height: 52px; z-index: 1; color: #f87171; background: #ffffff; border: 2px solid #f87171;"
                                : "width: 52px; height: 52px; z-index: 1; color: #3b82f6; background: #ffffff; border: 2px solid #3b82f6;",
                            type: "button",
                            title: isLive ? "End Call" : "Close",
                            listeners: {
                                click: () =>
                                    isLive ? this.endCall() : this.closeBubble(),
                            },
                        },
                        h.i({
                            class: isLive
                                ? "bi bi-telephone-fill"
                                : "fas fa-check",
                            style: "font-size: 1.1rem;",
                        }),
                    ),
                ),
                h.div(
                    {
                        class: "fw-semibold text-dark",
                        style: "font-size: 0.65rem;",
                    },
                    isLive ? "End Call" : "Close",
                ),
            ),
            // Watermark - credits used, no background, bottom-right.
            v(this.creditsCharged, (cc) =>
                h.div(
                    {
                        class: "text-end",
                        style: "font-size: 0.55rem; opacity: 0.5;",
                    },
                    `${cc.value} AI credits used`,
                ),
            ),
        );
    }

    /**
     * A soft ring (Carata brand purple) around the phone button that
     * grows/fades with how loud the agent is speaking right now
     * (0 = resting, 1 = loud). Kept purple rather than red so it never
     * reads as an error/alert - the end-call button is the only red
     * element in this card.
     */
    renderVoiceRing(level) {
        const scale = (1 + level * 0.9).toFixed(2);
        const opacity = (0.15 + level * 0.45).toFixed(2);

        return h.div({
            class: "position-absolute rounded-circle",
            style: `inset: 0; background: rgba(151, 62, 135, ${opacity}); transform: scale(${scale}); transition: transform 80ms linear, opacity 80ms linear; pointer-events: none;`,
        });
    }

    /**
     * Minimized in-call badge - a small green square (not a circle, so
     * it reads as distinct from the idle trigger) showing just the
     * elapsed time. Tap to bring the full card back.
     */
    renderMinimized(status) {
        return h.button(
            {
                class: "btn text-white rounded-3 d-flex flex-column align-items-center justify-content-center shadow-lg border-0 gap-0",
                style: "width: 52px; height: 52px; background: linear-gradient(135deg, #34d399 0%, #059669 100%);",
                type: "button",
                title: "Call in progress - tap to expand",
                listeners: { click: () => this.expand() },
            },
            h.i({ class: "bi bi-telephone-fill", style: "font-size: 0.8rem;" }),
            v(this.elapsedSeconds, (es) =>
                h.div(
                    { style: "font-size: 0.6rem; line-height: 1.2;" },
                    this.formatDuration(es.value),
                ),
            ),
        );
    }

    minimize() {
        this.minimized.value = true;
        this.resnap();
    }

    expand() {
        this.minimized.value = false;
        this.resnap();
    }

    statusLabel(status) {
        return (
            {
                connecting: "Connecting...",
                active: "Call Ongoing",
                ending: "Ending call...",
                ended: "Call ended",
                error: "Call error",
            }[status] ?? "Talk to Yoca"
        );
    }

    formatDuration(totalSeconds) {
        const seconds = Math.max(0, totalSeconds ?? 0);
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${String(s).padStart(2, "0")}`;
    }

    startCall() {
        this.transcript.value = [];
        this.billedSeconds.value = 0;
        this.creditsCharged.value = 0;
        this.errorMessage.value = null;
        this.audioLevel.value = 0;
        this.elapsedSeconds.value = 0;
        this.minimized.value = false;
        this.status.value = "connecting";
        this.resnap();

        broker.send($e.voiceOrderCall.needs.start, payload({}));
    }

    endCall() {
        this.status.value = "ending";
        broker.send($e.voiceOrderCall.needs.end, payload({}));
    }

    closeBubble() {
        this.clearEndTimer();
        this.stopTicker();
        this.status.value = "idle";
        this.minimized.value = false;
        this.resnap();
    }

    /**
     * Re-run the drag helper's own edge-snap after the bubble's content
     * (and therefore its size) changes, so switching between the idle
     * trigger, the prompt/call cards, and the minimized badge never
     * leaves part of it hanging off the screen.
     */
    resnap() {
        requestAnimationFrame(() => {
            const el = dom.id(WIDGET_ID);
            const instance = el && DraggableCore.instances.get(el);
            instance?.snap();
        });
    }

    clearEndTimer() {
        if (this.endTimer) {
            clearTimeout(this.endTimer);
            this.endTimer = null;
        }
    }

    /**
     * Start the local, second-by-second "time elapsed" clock. Driven off
     * wall-clock time (Date.now()) rather than counting up from
     * billedSeconds, so it ticks smoothly instead of jumping once per
     * heartbeat_interval_seconds.
     */
    startTicker() {
        if (this.tickInterval) return;
        this.callStartedAt = Date.now();
        this.elapsedSeconds.value = 0;
        this.tickInterval = setInterval(() => {
            this.elapsedSeconds.value = Math.floor(
                (Date.now() - this.callStartedAt) / 1000,
            );
        }, 1000);
    }

    stopTicker() {
        if (this.tickInterval) {
            clearInterval(this.tickInterval);
            this.tickInterval = null;
        }
    }

    /**
     * After the call ends (cleanly or on error), leave the summary up
     * briefly so the user can see the final time/credits, then fold
     * back down to the idle mic trigger on its own.
     */
    scheduleAutoClose() {
        this.clearEndTimer();
        this.endTimer = setTimeout(() => this.closeBubble(), 5000);
    }

    $$voiceOrderCall = {
        has: {
            statusChanged: (ed) => {
                const { message } = eParse(ed);
                const status = message.get("status");
                if (status) this.status.value = status;
                if (status === "active") this.startTicker();
                this.resnap();
            },

            transcript: (ed) => {
                const { message } = eParse(ed);
                const text = message.get("text");
                const speaker = message.get("speaker", "agent");
                if (!text) return;

                this.transcript.value = [
                    ...this.transcript.value,
                    { speaker, text },
                ];
            },

            heartbeat: (ed) => {
                const { message } = eParse(ed);
                this.billedSeconds.value = message.get(
                    "billedSeconds",
                    this.billedSeconds.value,
                );
                this.creditsCharged.value = message.get(
                    "creditsCharged",
                    this.creditsCharged.value,
                );
            },

            audioLevel: (ed) => {
                const { message } = eParse(ed);
                this.audioLevel.value = message.get(
                    "level",
                    this.audioLevel.value,
                );
            },

            ended: (ed) => {
                const { message } = eParse(ed);
                this.billedSeconds.value = message.get(
                    "billedSeconds",
                    this.billedSeconds.value,
                );
                this.creditsCharged.value = message.get(
                    "creditsCharged",
                    this.creditsCharged.value,
                );
                this.audioLevel.value = 0;
                this.minimized.value = false;
                this.stopTicker();
                this.status.value = "ended";
                this.resnap();
                this.scheduleAutoClose();
            },

            error: (ed) => {
                const { message } = eParse(ed);
                this.errorMessage.value = message.get(
                    "message",
                    "The voice call ran into a problem.",
                );
                this.audioLevel.value = 0;
                this.minimized.value = false;
                this.stopTicker();
                this.status.value = "error";
                this.resnap();
                this.scheduleAutoClose();
            },
        },
    };

    cleanUp() {
        super.cleanUp();
        this.clearEndTimer();
        this.stopTicker();
    }
}

ojs(VoiceOrderCallModal, Draggable);
